/**
 * Nutzungsstatistik (W15, D-054): meldet dem eigenen Server, welche Bildschirme und Funktionen
 * genutzt werden, wie lange, und welche Fehler auftreten. Ohne Inhalte: Namen sind Routen-Muster
 * und API-Pfade ohne IDs. Nur angemeldet und nur ohne Widerspruch (Einstellungen). Nichts wird im
 * Browser gespeichert; die Sitzung lebt nur, solange der Tab offen ist.
 */

export type Ereignis = {
  art: "bildschirm" | "aktion" | "fehler" | "absturz";
  name: string;
  dauer_ms?: number;
  daten?: Record<string, string | number | boolean | null>;
};
type Wartend = Ereignis & { zeit: number };

declare const __VERSION__: string;
/** W82 Beta-W: Stand des Baus (vite.config.ts, PG_STAND oder kurzer Hash), für die Build-Kennung der Rückmeldung. */
declare const __STAND__: string;

const SENDEN_ALLE_MS = 20_000;
const HOECHSTENS = 150;
const ID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\b\d{3,}\b/gi;

let erlaubt = false;
let csrf = "";
let warteschlange: Wartend[] = [];
let takt: ReturnType<typeof setInterval> | undefined;
/** Der offene Bildschirm; `seit` ist null, solange die Seite verborgen oder die Statistik aus ist. */
let bildschirm: { name: string; seit: number | null; fortgesetzt?: boolean } | null = null;
const sitzung = zufall();

function zufall(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return "w-" + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** In Tests und unter Fernsteuerung (Playwright) bleibt die Statistik aus, ausser sie wird
 *  ausdruecklich gewuenscht. */
function automatisiert(): boolean {
  return navigator.webdriver === true && !(window as { papiergaistNutzung?: boolean }).papiergaistNutzung;
}

/** W82 Beta-W: Build-Kennung der Rückmeldung, „0.1.0 (a1b2c3d4)“; der Server nimmt 1 bis 40 Zeichen aus
 *  `A-Z a-z 0-9 . ( ) _ + -` und Leerzeichen, alles andere wird „-“ (eine ungültige Angabe kippte sonst die ganze Rückmeldung). */
export function buildKennung(): string {
  return `${__VERSION__} (${__STAND__})`.replace(/[^A-Za-z0-9.()_+ -]/g, "-").slice(0, 40);
}

/** Namen ohne IDs: „/dokument/{id}“, „POST /api/dokumente/{id}/bestaetigen“. */
export function ohneIds(text: string): string {
  return text.replace(ID, "{id}").slice(0, 120);
}

/** Der Server nimmt als Namen nur Buchstaben, Ziffern und „ .:/{}-_“ an. */
function alsName(text: string): string {
  return ohneIds(text).replace(/[^\w .:/{}-]/g, "_") || "_";
}

/** Im Browser liest die Statistik nichts vom Geraet aus (§ 25 TDDDG, W17): Browser und System
 *  leitet der Server aus dem User-Agent-Header der Anfrage selbst ab und ignoriert diese Felder. */
function geraet(): { geraet: string; os: string } {
  return { geraet: "", os: "" };
}

export function melden(e: Ereignis): void {
  if (!erlaubt || automatisiert()) return;
  warteschlange.push({ ...e, name: alsName(e.name), zeit: Date.now() });
  if (warteschlange.length > HOECHSTENS) warteschlange = warteschlange.slice(-HOECHSTENS);
}

/** Die Verweildauer bis jetzt melden (Wechsel, Seite verborgen, Tab geschlossen). */
function abschliessen(): void {
  if (!bildschirm || bildschirm.seit === null) return;
  const dauer = Date.now() - bildschirm.seit;
  bildschirm.seit = null;
  melden({
    art: "bildschirm",
    name: bildschirm.name,
    dauer_ms: Math.min(Math.max(0, Math.round(dauer)), 24 * 3600 * 1000),
    // nach verborgenem Tab zaehlt die Zeit weiter, aber nicht als neuer Aufruf
    ...(bildschirm.fortgesetzt ? { daten: { fortgesetzt: true } } : {}),
  });
}

/** Bildschirm gewechselt: der vorige wird mit seiner sichtbaren Verweildauer gemeldet. */
export function bildschirmWechsel(name: string | null): void {
  abschliessen();
  bildschirm = name ? { name, seit: erlaubt && document.visibilityState === "visible" ? Date.now() : null } : null;
}

/** Jede schreibende Anfrage ist eine Aktion (client.ts ruft das auf). */
export function aktion(methode: string, pfad: string): void {
  if (methode === "GET" || pfad.startsWith("/nutzung")) return;
  melden({ art: "aktion", name: `${methode} /api${pfad.split("?")[0] ?? pfad}` });
}

export async function senden(letztes = false): Promise<void> {
  if (!erlaubt || !warteschlange.length || automatisiert()) return;
  const jetzt = Date.now();
  const paket = warteschlange;
  warteschlange = [];
  const koerper = JSON.stringify({
    sitzung,
    plattform: "web",
    version: __VERSION__,
    ...geraet(),
    ereignisse: paket.map(({ zeit, ...e }) => ({ ...e, vor_ms: Math.max(0, jetzt - zeit) })),
  });
  try {
    await fetch("/api/nutzung", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf },
      body: koerper,
      credentials: "same-origin",
      keepalive: letztes, // ueberlebt das Schliessen des Tabs
    });
  } catch {
    // Statistik ist nie wichtig genug fuer eine Fehlermeldung; das Paket ist verloren
  }
}

/** Seite verborgen oder Tab geschlossen: die Verweildauer endet hier und geht sofort hinaus
 *  (sonst fehlte der letzte Bildschirm jeder Sitzung). Wieder sichtbar: ab jetzt weiterzaehlen. */
function sichtbarkeit(): void {
  if (document.visibilityState === "hidden") verbergen();
  else if (bildschirm && bildschirm.seit === null) {
    bildschirm.seit = Date.now();
    bildschirm.fortgesetzt = true;
  }
}

function verbergen(): void {
  abschliessen();
  void senden(true);
}

/** Stelle im Programm aus dem Aufrufstapel: „index-AbC.js:L12C3456“. Die Meldung des Fehlers
 *  geht nicht mit, sie koennte Inhalte tragen (etwa ein Stueck einer Antwort des Servers). */
function ort(stapel: string | undefined): string {
  const treffer = /([\w.-]+\.(?:js|tsx?)):(\d+):(\d+)/.exec(stapel ?? "");
  return treffer ? `${treffer[1]}:L${treffer[2]}C${treffer[3]}` : "";
}

function fehler(e: ErrorEvent | PromiseRejectionEvent): void {
  const grund: unknown = "reason" in e ? e.reason : e.error;
  const name = grund instanceof Error ? grund.name : "reason" in e ? "Promise" : "Fehler";
  melden({ art: "fehler", name, daten: { ort: ort(grund instanceof Error ? grund.stack : undefined), wo: bildschirm?.name ?? "" } });
}

/** Ein- oder ausschalten (nach der Anmeldung, bei Widerspruch, nach dem Abmelden). */
export function nutzungZustand(an: boolean, token = ""): void {
  csrf = token || csrf;
  if (an === erlaubt) return;
  erlaubt = an;
  if (an) {
    takt = setInterval(() => void senden(), SENDEN_ALLE_MS);
    document.addEventListener("visibilitychange", sichtbarkeit);
    window.addEventListener("pagehide", verbergen);
    window.addEventListener("error", fehler);
    window.addEventListener("unhandledrejection", fehler);
    // der Bildschirm, der gerade offen ist, zaehlt ab jetzt
    if (bildschirm && document.visibilityState === "visible") bildschirm.seit = Date.now();
  } else {
    clearInterval(takt);
    document.removeEventListener("visibilitychange", sichtbarkeit);
    window.removeEventListener("pagehide", verbergen);
    window.removeEventListener("error", fehler);
    window.removeEventListener("unhandledrejection", fehler);
    warteschlange = [];
    if (bildschirm) bildschirm.seit = null;
  }
}

/** Nur fuer Tests. */
export function _zustand(): { warteschlange: Wartend[]; erlaubt: boolean } {
  return { warteschlange, erlaubt };
}
